import { AxiosResponseHeaders } from 'axios'

import { Option } from 'Shared/molecules/Form/FormSearchSelect'

import { TableUrlParams } from './pagination'

export type State<T> = {
	value: T | null
	isLoading: boolean
	isSaving?: boolean
}

export interface FlagFields {
	fieldName: string
	span: number
	isNextToLabel?: boolean
	disabled?: boolean
	tooltipText?: boolean
}

export interface CustomGenerator<TNext, TReturn = void>
	extends Generator<Promise<TNext>, TReturn, TNext> {}

export interface DefaultFile {
	file: File
	fileList: { originFileObj: File }[]
}

export type EmptyDefaultFile = {
	file: null
	fileList: []
}

export interface MultipleDefaultFile {
	file: null
	fileList: {
		originFileObj?: File
		uid: string
		name: string
	}[]
}

export interface EditTableConfig<
	SelectFieldNames extends string,
	InputFieldNames extends string,
	AutoSelectFieldNames extends string
> {
	selects: Record<
		SelectFieldNames,
		{
			options: LabelValue<number>[]
		}
	>
	inputs: Record<InputFieldNames, {}>
	autoSelect: Record<
		AutoSelectFieldNames,
		{
			onSearch: (params: TableUrlParams) => Promise<Option[]>
		}
	>
}

export interface SearchResult {
	searchById: Entity[]
	searchByName: Entity[]
}

export enum SearchResultFields {
	searchById = 'searchById',
	searchByName = 'searchByName'
}

export interface SearchResult {
	searchResultById: Entity[]
	searchResultByName: Entity[]
}

// TODO: should be refactored in scope of IS2-5476 refactoring ticket
export interface ContractNumbersAndMsasSearchResult {
	contracts: Entity[]
	msas: Entity[]
}

export interface AxiosErrorResponse {
	errors: { [key: string]: string[] }
	detail?: string
}

export interface InputMap<T> {
	fieldName: keyof T
}

export const USA = 'usa'

export enum OrderValues {
	asc = 'asc',
	desc = 'desc'
}
export const PAGE_TITLE = 'IS2'

export enum LoadingBtnNames {
	duplicate = 'duplicate',
	disposalFacilityAddress = 'disposalFacilityAddress',
	serviceTeamAdd = 'serviceTeam',
	serviceTeamCont = 'serviceTeamCont',
	certificateAdd = 'certificateAdd',
	certificateCont = 'certificateCont',
	companyAdd = 'companyAdd',
	companyCont = 'companyCont',
	manifestServiceAdd = 'manifestServiceAdd',
	manifestServiceBack = 'manifestServiceBack',
	manifestServiceItemAdd = 'manifestServiceItemAdd',
	manifestServiceItemBack = 'manifestServiceItemBack',
	parameterDelete = 'parameterDelete',
	parameterAdd = 'parameterAdd',
	generateReport = 'generateReport',
	sendReportToEmail = 'sendReportToEmail',
	addressValidate = 'addressValidate',
	addressAdd = 'addressAdd',
	serviceLocationAdd = 'serviceLocationAdd',
	serviceLocationCont = 'serviceLocationCont',
	supplierAdd = 'supplierAdd',
	supplierCont = 'supplierCont',
	supplierInvoiceQueueAdd = 'supplierInvoiceQueueAdd',
	supplierInvoiceQueueCont = 'supplierInvoiceQueueCont',
	supplierLocationAdd = 'supplierLocationAdd',
	supplierLocationCont = 'supplierLocationCont',
	purchaseOrderAdd = 'purchaseOrderAdd',
	purchaseOrderEdit = 'purchaseOrderEdit',
	exceptionAdd = 'exceptionAdd',
	commentAdd = 'commentAdd',
	reassign = 'reassign',
	resolveIssue = 'resolveIssue',
	resolveException = 'resolveException',
	outboundTrailerAdd = 'outboundTrailerAdd',
	outboundTrailerEdit = 'outboundTrailerEdit',
	rebuildTaxes = 'rebuildTaxes',
	lateFees = 'lateFees',
	markAsNonBillable = 'markAsNonBillable',
	backOutBillingItem = 'backOutBillingItem',
	cancelInvoice = 'cancelInvoice',
	addMultipleBale = 'addMultipleBale',
	addSingleBale = 'addSingleBale',
	supplierInvoiceLoad = 'supplierInvoiceLoad',
	customerInvoice = 'customerInvoice',
	updateService = 'updateService',
	createServiceManifestItem = 'createServiceManifestItem',
	updateHesWastestream = 'updateHesWastestream',
	completeServiceRequest = 'completeServiceRequest',
	save = 'save',
	loaderReady = 'loaderReady',
	moveToQueue = 'moveToQueue',
	uploadInvoiceAdd = 'uploadInvoiceAdd',
	uploadInvoiceCont = 'uploadInvoiceCont',
	convertToFee = 'convertToFee',
	submit = 'submit',
	updateLocation = 'updateLocation',
	deleteDocument = 'deleteDocument',
	complete = 'complete',
	document = 'document',
	approvePurchaseOrder = 'approvePurchaseOrder',
	rejectPurchaseOrder = 'rejectPurchaseOrder',
	updateBillingItem = 'updateBillingItem'
}

export type NotificationType = 'error' | 'warning' | 'success' | 'approved'

export type TransformResponseType = { data: Blob; headers: AxiosResponseHeaders }
export type TransformArrayBufferResponseType = { data: ArrayBuffer; headers: AxiosResponseHeaders }

export enum PreviewSections {
	Checkbox = 'checkbox',
	Find = 'find',
	Multiple = 'multiple',
	DateRange = 'range',
	Date = 'date',
	Text = 'text',
	Single = 'single'
}

export interface InfiniteLoaderData {
	from: number
	to: number
}

export const defaultInfiniteLoaderData: InfiniteLoaderData = {
	from: 21,
	to: 40
}

export enum UserType {
	INTERNAL = 'INTERNAL',
	CUSTOMER = 'CUSTOMER',
	SUPPLIER = 'SUPPLIER',
	DATA_MANAGEMENT = 'IS2DATA',
	NEW_SUPPLIER = 'NEWSUPPLIER',
	NEW_CUSTOMER = 'NEWCUSTOMER'
}

export enum UserTypeLabel {
	internal = 'Internal Customer Service Representative',
	supplier = 'Supplier',
	customer = 'Customer',
	dataManagement = 'Data Management',
	newSupplier = 'New Supplier',
	newCustomer = 'New Customer'
}

export type FieldNameRegexType =
	| `${string}.${number}.${string}`
	| `${string}.${number}`
	| `${string}[${number}].${string}`
	| `${string}.${string}`

export interface RequestDataAdditionalParams {
	loadPage: boolean
	calculateCount: boolean
}

export interface TableActions {
	isSorting?: boolean
	isPagination?: boolean
}

export const CREATED_BY_IS2 = 'IS2'
export const LAST_NAME_MAX_LENGTH = 7

export interface InternalContact extends Entity {
	email: BasicString
	phone: BasicString
}
